/**
 * Post moderation scenarios (final / locked).
 * Single source of truth for linked data: Businesses → Posts → Flags → Moderation cases.
 * Use only these 5 scenarios across Users, Posts, Moderation Queue, and Archive.
 */

// Base dates: 29 Jan 2026 as "today"
const BASE = new Date('2026-01-29T12:00:00.000Z');

const scenarios = [
  {
    scenarioKey: 1,
    name: 'Stolen / Copyrighted Images',
    business: {
      businessId: 'BUS-9934',
      businessName: 'Werribee Plumbing Pty Ltd',
      email: 'contact@werribeeplumbing.com.au',
      mobile: '0412 345 678',
      region: 'VIC',
      category: 'Plumbing',
      status: 'active',
      verificationStatus: 'verified',
      suburb: 'Werribee',
      postcode: '3030',
      joined: '2025-01-29',
    },
    post: {
      postId: 'POST-7712',
      title: 'Exceptional Service and Craftsmanship',
      description: 'We recently completed a full bathroom renovation in Werribee. This project included waterproofing, tiling, plumbing upgrades, and final fittings. High-quality materials were used and the job was completed on time.',
      images: [
        { caption: 'Bathroom renovation – before', url: 'https://picsum.photos/seed/post7712a/640/400' },
        { caption: 'Bathroom renovation – during', url: 'https://picsum.photos/seed/post7712b/640/400' },
        { caption: 'Bathroom renovation – after', url: 'https://picsum.photos/seed/post7712c/640/400' },
      ],
      suburb: 'Werribee',
      postcode: '3030',
      subcategory: 'Bathroom Renovation',
      postType: 'Featured',
      serviceRadius: '5 km from Werribee 3030',
      status: 'Active',
      publishedOn: '2025-10-22',
    },
    reporter: { name: 'James Wilson' },
    flag: {
      flagId: 'FLG-20491',
      source: 'User Report',
      category: 'Moderation',
      trigger: 'Reported stolen / copyrighted images',
      reason: 'Reported stolen / copyrighted images',
      severity: 'Medium',
      status: 'Closed',
      linkedCaseId: 'MOD-000241',
      createdAt: '2026-01-28T16:02:00.000Z',
    },
    case: {
      caseId: 'MOD-000241',
      contentType: 'Post',
      severity: 'Medium',
      status: 'Resolved',
      assignedTo: 'Alex Chen',
      created: '2026-01-28T16:03:00.000Z',
      updated: '2026-01-28T16:18:00.000Z',
      outcome: 'Violation Confirmed',
      enforcement: 'Remove Image(s)',
      escalatedTo: null,
      closedDate: '2026-01-28T16:18:00.000Z',
      closedBy: 'Alex Chen',
    },
    adminNote: 'Images appear to be stock photography and do not match the business\'s previous posts or portfolio. Reverse image checks indicate similar images used across unrelated websites. Business has not provided evidence of ownership. Media removal recommended to prevent misleading representation.',
  },
  {
    scenarioKey: 2,
    name: 'Prohibited Contact & Pricing',
    business: {
      businessId: 'BUS-4412',
      businessName: 'RapidFix Plumbing Pty Ltd',
      email: 'info@rapidfixplumbing.com.au',
      mobile: '0412 345 678',
      region: 'VIC',
      category: 'Plumbing',
      status: 'active',
      verificationStatus: 'verified',
      suburb: 'Werribee',
      postcode: '3030',
      joined: '2025-02-28',
    },
    post: {
      postId: 'POST-7789',
      title: 'Emergency Plumbing Services – Fast Response',
      description: 'We provide 24/7 emergency plumbing services across Werribee and surrounding suburbs. Call 0412 345 678 now for immediate assistance. Prices start from $199 depending on the job.',
      images: [
        { caption: 'Van with phone number visible', url: 'https://picsum.photos/seed/post7789a/640/400' },
        { caption: 'Technician at work', url: 'https://picsum.photos/seed/post7789b/640/400' },
        { caption: 'Company logo', url: 'https://picsum.photos/seed/post7789c/640/400' },
      ],
      suburb: 'Werribee',
      postcode: '3030',
      subcategory: 'Emergency Plumbing',
      postType: 'Standard',
      status: 'Active',
      publishedOn: '2026-01-24',
    },
    reporter: { name: 'Emma Davis' },
    flag: {
      flagId: 'FLG-20502',
      source: 'User Report',
      category: 'Content Control',
      trigger: 'Contact details and pricing included in post',
      reason: 'Contact details and pricing included in post',
      severity: 'Low',
      status: 'Under Review',
      linkedCaseId: 'MOD-000242',
      createdAt: '2026-01-28T17:01:00.000Z',
    },
    case: {
      caseId: 'MOD-000242',
      contentType: 'Post',
      severity: 'Low',
      status: 'Active',
      assignedTo: 'Alex Chen',
      created: '2026-01-28T17:01:00.000Z',
      updated: '2026-01-28T17:01:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Post contains a phone number and pricing information in both text and imagery. Platform policy requires all pricing and direct contact details to be handled through approved channels. First occurrence; edit request issued rather than removal.',
  },
  {
    scenarioKey: 3,
    name: 'Misleading / False Claims (Escalation)',
    business: {
      businessId: 'BUS-5521',
      businessName: 'Apex Gas & Plumbing',
      email: 'enquiries@apexgasplumbing.com.au',
      mobile: '0422 555 211',
      region: 'VIC',
      category: 'Plumbing',
      status: 'active',
      verificationStatus: 'verified',
      suburb: 'Hoppers Crossing',
      postcode: '3029',
      joined: '2025-03-30',
    },
    post: {
      postId: 'POST-7811',
      title: 'Licensed Gas Plumbing Experts',
      description: 'Our team is fully licensed and certified to handle all gas plumbing work across Victoria. We guarantee compliance with all regulations and standards.',
      images: [
        { caption: 'Technician working on gas line', url: 'https://picsum.photos/seed/post7811a/640/400' },
        { caption: 'Gas meter', url: 'https://picsum.photos/seed/post7811b/640/400' },
        { caption: 'Branded vehicle', url: 'https://picsum.photos/seed/post7811c/640/400' },
      ],
      suburb: 'Hoppers Crossing',
      postcode: '3029',
      subcategory: 'Gas Fitting',
      postType: 'Featured',
      status: 'Active',
      publishedOn: '2026-01-20',
    },
    reporter: { name: 'Daniel Morris' },
    flag: {
      flagId: 'FLG-20518',
      source: 'User Report',
      category: 'Fraud & Security',
      trigger: 'Potentially false licensing claims',
      reason: 'Misleading service claim in post copy',
      severity: 'High',
      status: 'Under Review',
      linkedCaseId: 'MOD-000243',
      createdAt: '2026-01-28T18:10:00.000Z',
    },
    case: {
      caseId: 'MOD-000243',
      contentType: 'Post',
      severity: 'High',
      status: 'Active',
      assignedTo: 'Alex Chen',
      created: '2026-01-28T18:10:00.000Z',
      updated: '2026-01-28T18:10:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: 'Compliance',
    },
    adminNote: 'Post makes claims of gas licensing and certification. These claims could not be verified through available business records and may mislead consumers regarding regulated services. Due to potential safety and regulatory implications, post was removed and case escalated to Compliance for licence verification.',
  },
  {
    scenarioKey: 4,
    name: 'Spam / Promotional Abuse',
    business: {
      businessId: 'BUS-6609',
      businessName: 'QuickFlow Plumbing Services',
      email: 'hello@quickflowplumbing.com.au',
      mobile: '0433 660 909',
      region: 'VIC',
      category: 'Plumbing',
      status: 'active',
      verificationStatus: 'verified',
      suburb: 'Werribee',
      postcode: '3030',
      joined: '2025-05-29',
    },
    post: {
      postId: 'POST-7856',
      title: 'Blocked Drain Service Available',
      description:
        'Blocked drain and general plumbing services available in Werribee and nearby suburbs. Same-day callouts are available where scheduling allows.',
      images: [
        { caption: 'Service vehicle photo', url: 'https://picsum.photos/seed/post7856a/640/400' },
        { caption: 'Drain-cleaning equipment photo', url: 'https://picsum.photos/seed/post7856b/640/400' },
        { caption: 'Completed pipe-repair photo', url: 'https://picsum.photos/seed/post7856c/640/400' },
      ],
      suburb: 'Werribee',
      postcode: '3030',
      subcategory: 'Drainage',
      postType: 'Standard',
      status: 'Active',
      publishedOn: '2026-01-27',
    },
    reporter: { name: 'Sarah Kerr' },
    flag: {
      flagId: 'FLG-52041',
      source: 'User Flag',
      category: 'Spam',
      trigger: 'Misleading service claim',
      reason: 'Misleading service claim',
      severity: 'Medium',
      status: 'Under Review',
      linkedCaseId: 'MOD-000244',
      createdAt: '2026-01-29T08:14:00.000Z',
    },
    /** Extra open Flags for this Post (Screen 1 open-count sample). Primary flag remains `flag` for Queue linkage. */
    extraFlags: [
      {
        flagId: 'FLG-52018',
        source: 'System Flag',
        category: 'Spam',
        trigger: 'Repeated promotional wording',
        reason: 'Repeated promotional wording',
        severity: 'Low',
        status: 'Under Review',
        linkedCaseId: 'MOD-000238',
        createdAt: '2026-01-28T17:33:00.000Z',
        lastUpdated: '2026-01-29T09:20:00.000Z',
        caseStatus: 'Active',
      },
    ],
    case: {
      caseId: 'MOD-000244',
      contentType: 'Post',
      severity: 'Medium',
      status: 'New',
      assignedTo: null,
      created: '2026-01-29T08:14:00.000Z',
      updated: '2026-01-29T10:15:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Business has published multiple near-identical promotional posts within a short timeframe. Content repetition and frequency indicate spam behaviour rather than legitimate updates. Temporary posting restriction applied to prevent feed abuse.',
  },
  {
    scenarioKey: 5,
    name: 'No Violation / False Report',
    business: {
      businessId: 'BUS-7018',
      businessName: 'HomeFix Plumbing Solutions',
      email: 'contact@homefixplumbing.com.au',
      mobile: '0444 701 818',
      region: 'VIC',
      category: 'Plumbing',
      status: 'active',
      verificationStatus: 'verified',
      suburb: 'Werribee',
      postcode: '3030',
      joined: '2025-06-29',
    },
    post: {
      postId: 'POST-7920',
      title: 'Complete Kitchen Plumbing Upgrade',
      description: 'We recently completed a full kitchen plumbing upgrade, including new piping, fixtures, and drainage improvements. All work was completed according to Australian standards.',
      images: [
        { caption: 'Kitchen sink installation', url: 'https://picsum.photos/seed/post7920a/640/400' },
        { caption: 'Under-sink piping', url: 'https://picsum.photos/seed/post7920b/640/400' },
        { caption: 'Finished kitchen setup', url: 'https://picsum.photos/seed/post7920c/640/400' },
      ],
      suburb: 'Werribee',
      postcode: '3030',
      subcategory: 'Kitchen Plumbing',
      postType: 'Standard',
      status: 'Active',
      publishedOn: '2026-01-18',
    },
    reporter: { name: 'Michael Turner' },
    flag: {
      flagId: 'FLG-20566',
      source: 'User Report',
      category: 'Moderation',
      trigger: 'Reported misleading images',
      reason: 'Reported misleading images',
      severity: 'Low',
      status: 'Closed',
      linkedCaseId: 'MOD-000245',
      createdAt: '2026-01-29T11:02:00.000Z',
    },
    case: {
      caseId: 'MOD-000245',
      contentType: 'Post',
      severity: 'Low',
      status: 'Resolved',
      assignedTo: 'Alex Chen',
      created: '2026-01-29T11:02:00.000Z',
      updated: '2026-01-29T11:14:00.000Z',
      outcome: 'No Violation',
      enforcement: null,
      escalatedTo: null,
      closedDate: '2026-01-29T11:14:00.000Z',
      closedBy: 'Alex Chen',
    },
    adminNote: 'Reviewed post content and images. Images appear consistent with business\'s previous work and do not show signs of stock or reused media. No misleading claims identified. Report appears to be unfounded. Case closed with no action.',
  },
];

export function getScenarioByCaseId(caseId) {
  const normalized = String(caseId).replace(/^(?:MODS|MOA|MOD)-?/i, '');
  return (
    scenarios.find(
      (s) => String(s.case.caseId).replace(/^(?:MODS|MOA|MOD)-?/i, '') === normalized
    ) || null
  );
}

export function getScenarioByPostId(postId) {
  const normalized = String(postId).replace(/^POST-?/i, '');
  return scenarios.find((s) => String(s.post.postId).replace(/^POST-?/i, '') === normalized) || null;
}

export function getScenarioByFlagId(flagId) {
  const normalized = String(flagId).replace(/^FLG-?/i, '');
  return (
    scenarios.find((s) => {
      if (String(s.flag.flagId).replace(/^FLG-?/i, '') === normalized) return true;
      return (s.extraFlags || []).some(
        (ef) => String(ef.flagId).replace(/^FLG-?/i, '') === normalized
      );
    }) || null
  );
}

/** Map legacy flag sources to Moderation Queue Case Source values. */
function resolvePostCaseSource(flag) {
  const source = String(flag?.source || '').toLowerCase();
  const reason = String(flag?.reason || flag?.trigger || '').toLowerCase();
  if (source.includes('detection') || reason.includes('repeated promotional') || reason.includes('pattern')) {
    return 'Detection';
  }
  if (source === 'system' || source.includes('system')) return 'System Signal';
  return 'User Flag';
}

/** Moderation Queue — open + Decided (pending archive handoff), same as Compliance Queue */
export function getQueueCases() {
  return scenarios
    .map((s, idx) => {
      const reviewReason = s.flag.reason || s.flag.trigger;
      const caseSource = resolvePostCaseSource(s.flag);
      const flagCount = caseSource === 'User Flag' ? (s.scenarioKey === 3 ? 2 : 1) : null;
      return {
        id: s.case.caseId,
        caseId: s.case.caseId,
        contentType: 'Post',
        preview: s.post.title || s.post.description?.slice(0, 80) + (s.post.description?.length > 80 ? '…' : '') || '—',
        caseSource,
        reviewReason,
        flag: caseSource,
        reason: reviewReason,
        flagReason: reviewReason,
        flagCount,
        priority: s.case.severity,
        severity: s.case.severity,
        status: s.case.status === 'In Review' ? 'Under Review' : s.case.status,
        escalation: 'None',
        escalationLevel: 'None',
        assignedTo: s.case.assignedTo,
        created: s.case.created,
        updated: s.case.updated,
        postId: s.post.postId,
        postTitle: s.post.title,
        businessId: s.business.businessId,
        businessName: s.business.businessName,
        accountName: s.business.businessName,
        accountAvatar: `https://i.pravatar.cc/150?img=${idx + 10}`,
        flagId: s.flag.flagId,
        scenarioKey: s.scenarioKey,
      };
    });
}

/**
 * Moderation Archive (Screen 1) — closed cases dataset aligned with product spec.
 * Also used for navigation stubs (partiesInvolved, IDs).
 */
/**
 * Moderation Archive sample rows — same Case ID as Queue (MOD-######).
 * Covers every Case Type, both Decision Outcomes, and Customer | Business | System sources.
 * Transfer outcomes are excluded (they leave Moderation, no Archive entry).
 */
export const MODERATION_ARCHIVE_CASES = [
  // Post · Customer · Confirmed (links post scenario MOD-000241)
  {
    caseId: 'MOD-000241',
    queueCaseId: 'MOD-000241',
    contentType: 'Post',
    source: 'Customer',
    flagId: 'FLG-20491',
    flagReason: 'Reported stolen / copyrighted images',
    decisionOutcome: 'Confirmed',
    severity: 'Medium',
    contentId: 'POST-7712',
    postId: 'POST-7712',
    postTitle: 'Exceptional Service and Craftsmanship',
    caseCreated: '2026-01-28T16:03:00.000Z',
    accountName: 'Werribee Plumbing Pty Ltd',
    accountAvatar: 'https://i.pravatar.cc/150?img=11',
    accountType: 'Business',
    businessId: 'BUS-9934',
    assignedTo: 'Alex Chen',
    closedBy: 'Alex Chen',
    finalisedBy: 'Alex Chen',
    closedDate: '2026-01-28T16:18:00.000Z',
    finalisedOn: '2026-01-28T16:18:00.000Z',
    relatedAppealId: null,
  },
  // Post · Customer · Not Confirmed (links post scenario MOD-000245)
  {
    caseId: 'MOD-000245',
    queueCaseId: 'MOD-000245',
    contentType: 'Post',
    source: 'Customer',
    flagId: 'FLG-20566',
    flagReason: 'Reported misleading images',
    decisionOutcome: 'Not Confirmed',
    severity: 'Low',
    contentId: 'POST-7920',
    postId: 'POST-7920',
    postTitle: 'Complete Kitchen Plumbing Upgrade',
    caseCreated: '2026-01-29T11:02:00.000Z',
    accountName: 'HomeFix Plumbing Solutions',
    accountAvatar: 'https://i.pravatar.cc/150?img=15',
    accountType: 'Business',
    businessId: 'BUS-7018',
    assignedTo: 'Alex Chen',
    closedBy: 'Alex Chen',
    finalisedBy: 'Alex Chen',
    closedDate: '2026-01-29T11:14:00.000Z',
    finalisedOn: '2026-01-29T11:14:00.000Z',
    relatedAppealId: null,
  },
  // Review · System · Confirmed
  {
    caseId: 'MOD-000810',
    queueCaseId: 'MOD-000810',
    contentType: 'Review',
    source: 'System',
    flagId: 'FLG-62080',
    flagReason: 'Harassment / abusive language',
    decisionOutcome: 'Confirmed',
    severity: 'Medium',
    contentId: 'RVW-9201',
    reviewId: 'RVW-9201',
    caseCreated: '2026-01-20T09:10:00.000Z',
    accountName: 'Jordan P.',
    accountAvatar: 'https://i.pravatar.cc/150?img=45',
    accountType: 'Customer',
    customerId: 'CUS-9201',
    businessId: 'BUS-4412',
    assignedTo: 'Sarah Johnson',
    closedBy: 'Sarah Johnson',
    finalisedBy: 'Sarah Johnson',
    closedDate: '2026-01-21T14:30:00.000Z',
    finalisedOn: '2026-01-21T14:30:00.000Z',
    relatedAppealId: 'APL-000389',
  },
  // Business Reply · System · Not Confirmed
  {
    caseId: 'MOD-000811',
    queueCaseId: 'MOD-000811',
    contentType: 'Business Reply',
    source: 'System',
    flagId: 'FLG-62081',
    flagReason: 'Unprofessional wording',
    decisionOutcome: 'Not Confirmed',
    severity: 'Low',
    contentId: 'RRP-000501',
    reviewId: 'RVW-9202',
    caseCreated: '2026-01-22T11:00:00.000Z',
    accountName: 'RapidFix Plumbing Pty Ltd',
    accountAvatar: 'https://i.pravatar.cc/150?img=33',
    accountType: 'Business',
    businessId: 'BUS-4412',
    assignedTo: 'Mike Davis',
    closedBy: 'Mike Davis',
    finalisedBy: 'Mike Davis',
    closedDate: '2026-01-22T16:45:00.000Z',
    finalisedOn: '2026-01-22T16:45:00.000Z',
    relatedAppealId: null,
  },
  // Message · Customer · Not Confirmed (links message scenario MOD-000703)
  {
    caseId: 'MOD-000703',
    queueCaseId: 'MOD-000703',
    contentType: 'Message',
    source: 'Customer',
    flagId: 'FLG-50103',
    flagReason: 'Reported out of frustration — price negotiation / scheduling',
    decisionOutcome: 'Not Confirmed',
    severity: 'Low',
    contentId: 'MTH-7110',
    messageId: 'MTH-7110',
    caseCreated: '2026-01-30T10:00:00.000Z',
    accountName: 'RapidFix Plumbing Pty Ltd',
    accountAvatar: 'https://i.pravatar.cc/150?img=22',
    accountType: 'Business',
    businessId: 'BUS-4412',
    customerId: 'CUS-3349',
    assignedTo: 'Alex Chen',
    closedBy: 'Alex Chen',
    finalisedBy: 'Alex Chen',
    closedDate: '2026-01-30T10:05:00.000Z',
    finalisedOn: '2026-01-30T10:05:00.000Z',
    relatedAppealId: null,
  },
  // Message · Business · Confirmed
  {
    caseId: 'MOD-000812',
    queueCaseId: 'MOD-000812',
    contentType: 'Message',
    source: 'Business',
    flagId: 'FLG-50112',
    flagReason: 'Spam or promotional misuse in message thread',
    decisionOutcome: 'Confirmed',
    severity: 'High',
    contentId: 'MTH-7205',
    messageId: 'MTH-7205',
    caseCreated: '2026-01-18T08:20:00.000Z',
    accountName: 'Bright Spark Electrical',
    accountAvatar: 'https://i.pravatar.cc/150?img=28',
    accountType: 'Business',
    businessId: 'BUS-5502',
    assignedTo: 'John Smith',
    closedBy: 'John Smith',
    finalisedBy: 'John Smith',
    closedDate: '2026-01-19T12:00:00.000Z',
    finalisedOn: '2026-01-19T12:00:00.000Z',
    relatedAppealId: null,
  },
  // Quote · Customer · Not Confirmed
  {
    caseId: 'MOD-000553',
    queueCaseId: 'MOD-000553',
    contentType: 'Quote',
    source: 'Customer',
    flagId: 'FLG-55103',
    flagReason: 'Reported — price discussion / declined quote',
    decisionOutcome: 'Not Confirmed',
    severity: 'Medium',
    contentId: 'QUOTE-8025',
    quoteId: 'QUOTE-8025',
    quoteTitle: 'General Plumbing Repair',
    caseCreated: '2026-01-30T07:40:00.000Z',
    accountName: 'RapidFix Plumbing Pty Ltd',
    accountAvatar: 'https://i.pravatar.cc/150?img=66',
    accountType: 'Business',
    businessId: 'BUS-4412',
    customerId: 'CUS-3349',
    assignedTo: 'Alex Chen',
    closedBy: 'Alex Chen',
    finalisedBy: 'Alex Chen',
    closedDate: '2026-01-30T08:10:00.000Z',
    finalisedOn: '2026-01-30T08:10:00.000Z',
    relatedAppealId: null,
  },
  // Quote · Business · Confirmed
  {
    caseId: 'MOD-000813',
    queueCaseId: 'MOD-000813',
    contentType: 'Quote',
    source: 'Business',
    flagId: 'FLG-55113',
    flagReason: 'Business attempting to move customer off-platform',
    decisionOutcome: 'Confirmed',
    severity: 'High',
    contentId: 'QUOTE-8110',
    quoteId: 'QUOTE-8110',
    quoteTitle: 'Bathroom Renovation Quote',
    caseCreated: '2026-01-15T13:00:00.000Z',
    accountName: 'Apex Gas & Plumbing',
    accountAvatar: 'https://i.pravatar.cc/150?img=40',
    accountType: 'Business',
    businessId: 'BUS-5521',
    customerId: 'CUS-4102',
    assignedTo: 'Emma Wilson',
    closedBy: 'Emma Wilson',
    finalisedBy: 'Emma Wilson',
    closedDate: '2026-01-16T09:25:00.000Z',
    finalisedOn: '2026-01-16T09:25:00.000Z',
    relatedAppealId: null,
  },
  // Business Profile Item · System · Confirmed
  {
    caseId: 'MOD-000814',
    queueCaseId: 'MOD-000814',
    contentType: 'Business Profile Item',
    source: 'System',
    flagId: 'FLG-58014',
    flagReason: 'Prohibited contact details in profile description',
    decisionOutcome: 'Confirmed',
    severity: 'Medium',
    contentId: 'BPI-0140',
    caseCreated: '2026-01-12T10:00:00.000Z',
    accountName: 'QuickFlow Plumbing Services',
    accountAvatar: 'https://i.pravatar.cc/150?img=60',
    accountType: 'Business',
    businessId: 'BUS-6609',
    assignedTo: 'Sarah Johnson',
    closedBy: 'Sarah Johnson',
    finalisedBy: 'Sarah Johnson',
    closedDate: '2026-01-13T11:40:00.000Z',
    finalisedOn: '2026-01-13T11:40:00.000Z',
    relatedAppealId: null,
  },
  // Business Profile Item · System · Not Confirmed
  {
    caseId: 'MOD-000815',
    queueCaseId: 'MOD-000815',
    contentType: 'Business Profile Item',
    source: 'System',
    flagId: 'FLG-58015',
    flagReason: 'Suspected stock portfolio image',
    decisionOutcome: 'Not Confirmed',
    severity: 'Low',
    contentId: 'BPI-0141',
    caseCreated: '2026-01-10T08:15:00.000Z',
    accountName: 'HomeFix Plumbing Solutions',
    accountAvatar: 'https://i.pravatar.cc/150?img=61',
    accountType: 'Business',
    businessId: 'BUS-7018',
    assignedTo: 'Alex Chen',
    closedBy: 'Alex Chen',
    finalisedBy: 'Alex Chen',
    closedDate: '2026-01-11T15:05:00.000Z',
    finalisedOn: '2026-01-11T15:05:00.000Z',
    relatedAppealId: null,
  },
];

/** Moderation Archive list rows */
export function getArchiveCases() {
  return MODERATION_ARCHIVE_CASES;
}

/** Full archive row for Overview tab when opened by case id */
export function getArchiveCaseByCaseId(caseId) {
  if (!caseId) return null;
  const n = String(caseId).replace(/^(?:MODS|MOA|MOD)-?/i, '');
  return (
    MODERATION_ARCHIVE_CASES.find((r) => {
      const id = String(r.caseId || '').replace(/^(?:MODS|MOA|MOD)-?/i, '');
      const queueId = String(r.queueCaseId || '').replace(/^(?:MODS|MOA|MOD)-?/i, '');
      return id === n || queueId === n;
    }) || null
  );
}

/** Case IDs already in Archive — exclude from Queue list. */
export function getArchivedModerationCaseIdSet() {
  const ids = new Set();
  MODERATION_ARCHIVE_CASES.forEach((row) => {
    [row.caseId, row.queueCaseId].forEach((id) => {
      const n = String(id || '').replace(/^(?:MODS|MOA|MOD)-?/i, '');
      if (n) ids.add(n);
    });
  });
  return ids;
}

/** Businesses list: only scenario businesses */
export function getBusinessesForScenarios() {
  const now = new Date(BASE);
  return scenarios.map((s, idx) => {
    const b = s.business;
    const joined = new Date(now);
    joined.setDate(joined.getDate() - (365 - idx * 30));
    return {
      id: b.businessId,
      businessId: b.businessId,
      businessName: b.businessName,
      mobile: b.mobile,
      email: b.email,
      businessEmail: b.email,
      abn: `12${String(345678901 + idx).padStart(9, '0')}`,
      licenseNumber: `LIC${String(100000 + idx).padStart(6, '0')}`,
      location: { suburb: b.suburb, postcode: b.postcode },
      region: b.region,
      category: b.category,
      primaryTrade: b.category,
      status: b.status,
      plan: 'PRO',
      verificationStatus: b.verificationStatus,
      emailVerified: true,
      phoneVerified: true,
      emailInvalid: false,
      phoneInvalid: false,
      abnVerified: true,
      licenseVerified: true,
      joined: joined.toISOString().split('T')[0],
      lastLogin: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      lastActive: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      subscriptionStatus: 'Active — Pro',
      planType: 'Annual',
      moderationFlags: s.case.status === 'Resolved' ? 0 : 1,
      fraudAlerts: 0,
      roleType: 'business',
      profilePicture: `https://i.pravatar.cc/150?img=${idx + 10}`,
      primaryContactNumber: b.mobile,
      startDate: joined.toISOString().split('T')[0],
    };
  });
}

/** Open Flags: distinct Post Flags whose cases are New or Active. */
function isOpenFlagStatus(status) {
  const st = String(status || '').trim().toLowerCase();
  if (!st) return false;
  if (['decided', 'closed', 'resolved', 'cleared', 'approved'].includes(st)) return false;
  if (['new', 'active', 'under review', 'in review', 'open', 'pending', 'attached'].includes(st)) {
    return true;
  }
  return false;
}

/** Derive Admin Posts status from open handling / retention outcomes. */
function deriveAdminPostStatus(scenario) {
  const explicit = scenario?.post?.adminStatus || scenario?.post?.listStatus;
  if (
    explicit === 'Removed' ||
    explicit === 'Deleted' ||
    explicit === 'Draft' ||
    explicit === 'Active' ||
    explicit === 'Under Review'
  ) {
    return explicit;
  }
  const caseStatus = scenario?.case?.status;
  const flagStatus = scenario?.flag?.status;
  if (caseStatus === 'In Review' || caseStatus === 'New' || caseStatus === 'Active' || flagStatus === 'Under Review') {
    return 'Under Review';
  }
  // Confirmed Moderation returns the Post to Draft; do not invent a new Removed outcome.
  const outcome = String(scenario?.case?.outcome || '');
  if (caseStatus === 'Resolved' && /violation\s*confirmed|confirmed/i.test(outcome)) {
    return 'Draft';
  }
  // Retain an existing Removed value only when historically recorded on the Post.
  const enforcement = String(scenario?.case?.enforcement || '');
  if (scenario?.post?.removedOn && caseStatus === 'Resolved' && /remove\s*post/i.test(enforcement)) {
    return 'Removed';
  }
  return 'Active';
}

/**
 * Any accepted open Flag immediately hides the Post (including Low / New).
 * Draft, Removed and Deleted are always Hidden.
 */
function deriveAdminPostVisibility(status, flags) {
  if (status === 'Removed' || status === 'Deleted' || status === 'Draft') return 'Hidden';

  const hasOpenFlag = (Array.isArray(flags) ? flags : []).some((f) =>
    isOpenFlagStatus(f.handlingStatus || f.status || f.flagStatus || f.caseStatus)
  );
  if (hasOpenFlag) return 'Hidden';
  return 'Visible';
}

function mapScenarioFlagRow(f, s, fallbackCaseStatus) {
  const caseStatus = f.caseStatus || fallbackCaseStatus || s.case?.status;
  const openLike =
    caseStatus === 'In Review' ||
    caseStatus === 'New' ||
    caseStatus === 'Active' ||
    f.status === 'Under Review' ||
    f.status === 'Open' ||
    f.status === 'Active' ||
    f.status === 'New';
  const decidedLike = f.status === 'Closed' || caseStatus === 'Resolved' || caseStatus === 'Decided';
  const handlingStatus = decidedLike
    ? 'Decided'
    : caseStatus === 'New' || f.status === 'New' || f.status === 'Open'
      ? 'New'
      : 'Active';
  return {
    flagId: f.flagId,
    id: f.flagId,
    source: f.source,
    category: f.category,
    trigger: f.trigger,
    reason: f.reason || f.trigger,
    severity: f.severity || s.case?.severity,
    status: decidedLike ? 'Decided' : openLike ? (handlingStatus === 'New' ? 'New' : 'Active') : f.status,
    flagStatus: f.status,
    handlingStatus,
    caseStatus: decidedLike ? 'Decided' : handlingStatus,
    linkedCaseId: f.linkedCaseId || s.case?.caseId,
    linkedCaseType: 'Moderation',
    createdAt: f.createdAt,
    date: f.createdAt,
    lastUpdated: f.lastUpdated || s.case?.updated || f.createdAt,
    updatedAt: f.lastUpdated || s.case?.updated || f.createdAt,
  };
}

function buildScenarioListPost(s, idx, now) {
  const p = s.post;
  const f = s.flag;
  const published = new Date(p.publishedOn);
  const created = new Date(published);
  const updated = new Date(now);
  updated.setHours(10 - idx, 15 + idx * 5, 0, 0);
  if (idx > 0) updated.setDate(updated.getDate() - Math.floor(idx / 2));

  const flagStatusForFilter =
    f.status === 'Under Review' ? 'Under Review' : f.status === 'Closed' ? 'Resolved' : 'None';
  const moderationCaseStatus =
    s.case.status === 'Resolved'
      ? 'Decided'
      : s.case.status === 'New'
        ? 'New'
        : s.case.status === 'In Review' || s.case.status === 'Active'
          ? 'Active'
          : s.case.status;
  const businessPlan = idx % 3 === 0 ? 'Basic' : idx % 3 === 1 ? 'Pro' : 'Premium';

  const flags = [
    mapScenarioFlagRow(f, s),
    ...(Array.isArray(s.extraFlags) ? s.extraFlags.map((ef) => mapScenarioFlagRow(ef, s, ef.caseStatus)) : []),
  ];

  const status = deriveAdminPostStatus(s);
  const visibility = deriveAdminPostVisibility(status, flags);
  const keywordSeeds = {
    'Bathroom Renovation': ['Bathroom renovation', 'Waterproofing'],
    'Emergency Plumbing': ['Emergency plumbing', 'Same-day callout'],
    'Gas Fitting': ['Gas fitting', 'Licensed gas'],
    Drainage: ['Blocked drain', 'Drain cleaning'],
    'Kitchen Plumbing': ['Kitchen plumbing', 'Fixture upgrade'],
    'Interior Painting': ['Interior painting', 'Same-day painting'],
    Decking: ['Deck restoration', 'Timber care'],
    'Emergency Electrical': ['Emergency electrical', 'Same-day electrical'],
  };
  const appliedKeywords =
    p.appliedKeywords ||
    keywordSeeds[p.subcategory] ||
    (p.subcategory ? [p.subcategory] : []);

  return {
    id: p.postId,
    postId: p.postId,
    title: p.title,
    businessName: s.business.businessName,
    businessId: s.business.businessId,
    category: s.business.category,
    subcategory: p.subcategory || '—',
    appliedKeywords,
    suburb: p.suburb,
    postcode: p.postcode || s.business.postcode,
    serviceRadius: p.serviceRadius || '20 km',
    tier: p.postType === 'Featured' ? 'Featured' : 'Standard',
    postType: p.postType === 'Featured' ? 'Featured Post' : 'Post',
    status,
    visibility,
    flagStatus: flagStatusForFilter,
    region: s.business.region,
    state: s.business.region,
    createdAt: created.toISOString(),
    publishedOn: published.toISOString(),
    lastUpdated: updated.toISOString(),
    lastRefreshed: updated.toISOString(),
    rankingState: 'Fresh',
    views: 1000 + idx * 200,
    saves: 20 + idx * 5,
    shares: 3 + idx,
    messages: 8 + idx * 3,
    totalImages: p.images.length,
    businessAccountStatus: 'Active',
    businessVerification: 'Verified',
    businessMemberSince: s.business.joined || null,
    logo: `https://i.pravatar.cc/100?img=${idx + 10}`,
    thumbnail: p.images[0]?.url || `https://picsum.photos/seed/${p.postId}/64/64`,
    publicLink: `https://tradesposter.com/posts/${p.postId}`,
    description: p.description,
    gallery: (p.images || []).map((img) => ({ url: img.url, flagged: false })),
    scenarioKey: s.scenarioKey,
    moderationCaseId: s.case.caseId,
    moderationCaseStatus,
    businessPlan,
    flags,
  };
}

/** Retention / Screen 1 sample rows (Removed, Deleted, Draft, extra Under Review). Not moderation scenarios. */
function getRetentionSamplePosts(now) {
  return [
    {
      id: 'POST-7794',
      postId: 'POST-7794',
      title: 'Emergency Electrical Repairs',
      businessName: 'Bright Spark Electrical',
      businessId: 'BUS-8301',
      category: 'Electrical',
      subcategory: 'Emergency Electrical',
      suburb: 'Geelong',
      postcode: '3220',
      serviceRadius: '20 km',
      appliedKeywords: ['Emergency electrical', 'Same-day electrical'],
      tier: 'Featured',
      postType: 'Featured Post',
      status: 'Under Review',
      visibility: 'Hidden',
      flagStatus: 'Under Review',
      region: 'VIC',
      state: 'VIC',
      createdAt: new Date('2026-01-12T08:00:00.000Z').toISOString(),
      publishedOn: new Date('2026-01-12T08:20:00.000Z').toISOString(),
      lastUpdated: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(),
      lastRefreshed: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(),
      rankingState: 'Fresh',
      views: 890,
      saves: 14,
      shares: 2,
      messages: 5,
      totalImages: 2,
      businessAccountStatus: 'Active',
      businessVerification: 'Verified',
      businessMemberSince: '2025-03-08',
      logo: 'https://i.pravatar.cc/100?img=33',
      thumbnail: 'https://picsum.photos/seed/post7794/64/64',
      publicLink: 'https://tradesposter.com/posts/POST-7794',
      description: 'Emergency electrical repairs across Geelong and nearby suburbs. Same-day callouts where scheduling allows.',
      gallery: [
        { url: 'https://picsum.photos/seed/post7794a/640/400', flagged: false },
        { url: 'https://picsum.photos/seed/post7794b/640/400', flagged: false },
      ],
      scenarioKey: null,
      moderationCaseId: 'MOD-000250',
      moderationCaseStatus: 'Active',
      businessPlan: 'Pro',
      flags: [
        {
          flagId: 'FLG-52101',
          id: 'FLG-52101',
          source: 'User Flag',
          category: 'Moderation',
          reason: 'Unsafe electrical advice',
          severity: 'High',
          status: 'Active',
          flagStatus: 'Under Review',
          handlingStatus: 'Active',
          caseStatus: 'Active',
          linkedCaseId: 'MOD-000250',
          linkedCaseType: 'Moderation',
          createdAt: new Date(now.getTime() - 6 * 60 * 60 * 1000).toISOString(),
          lastUpdated: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(),
        },
      ],
    },
    {
      id: 'POST-7728',
      postId: 'POST-7728',
      title: 'Same-Day Painting Services',
      businessName: 'Premier Painting',
      businessId: 'BUS-8102',
      category: 'Painting',
      subcategory: 'Interior Painting',
      suburb: 'Ballarat',
      postcode: '3350',
      serviceRadius: '25 km',
      appliedKeywords: ['Interior painting', 'Same-day painting'],
      tier: 'Standard',
      postType: 'Post',
      status: 'Removed',
      visibility: 'Hidden',
      flagStatus: 'None',
      region: 'VIC',
      state: 'VIC',
      createdAt: new Date('2026-01-10T09:00:00.000Z').toISOString(),
      publishedOn: new Date('2026-01-10T09:15:00.000Z').toISOString(),
      lastUpdated: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      lastRefreshed: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      rankingState: 'Archived',
      views: 420,
      saves: 8,
      shares: 1,
      messages: 3,
      totalImages: 2,
      businessAccountStatus: 'Active',
      businessVerification: 'Verified',
      businessMemberSince: '2025-04-12',
      logo: 'https://i.pravatar.cc/100?img=22',
      thumbnail: 'https://picsum.photos/seed/post7728/64/64',
      publicLink: 'https://tradesposter.com/posts/POST-7728',
      description: 'Same-day interior painting available across Ballarat and nearby suburbs.',
      gallery: [
        { url: 'https://picsum.photos/seed/post7728a/640/400', flagged: false },
        { url: 'https://picsum.photos/seed/post7728b/640/400', flagged: false },
      ],
      scenarioKey: null,
      moderationCaseId: null,
      moderationCaseStatus: null,
      businessPlan: 'Pro',
      flags: [],
      removedOn: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'POST-7682',
      postId: 'POST-7682',
      title: 'Deck Restoration Service',
      businessName: 'Coastal Carpentry',
      businessId: 'BUS-8205',
      category: 'Carpentry',
      subcategory: 'Decking',
      suburb: 'Frankston',
      postcode: '3199',
      serviceRadius: '15 km',
      appliedKeywords: ['Deck restoration', 'Timber care'],
      tier: 'Standard',
      postType: 'Post',
      status: 'Deleted',
      visibility: 'Hidden',
      flagStatus: 'None',
      region: 'VIC',
      state: 'VIC',
      createdAt: new Date('2025-12-18T11:00:00.000Z').toISOString(),
      publishedOn: new Date('2025-12-18T11:20:00.000Z').toISOString(),
      lastUpdated: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastRefreshed: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      rankingState: 'Archived',
      views: 310,
      saves: 4,
      shares: 0,
      messages: 0,
      totalImages: 2,
      businessAccountStatus: 'Active',
      businessVerification: 'Verified',
      businessMemberSince: '2025-02-01',
      logo: 'https://i.pravatar.cc/100?img=28',
      thumbnail: 'https://picsum.photos/seed/post7682/64/64',
      publicLink: 'https://tradesposter.com/posts/POST-7682',
      description: 'Deck restoration and timber care for Frankston and surrounding suburbs.',
      gallery: [
        { url: 'https://picsum.photos/seed/post7682a/640/400', flagged: false },
        { url: 'https://picsum.photos/seed/post7682b/640/400', flagged: false },
      ],
      scenarioKey: null,
      moderationCaseId: null,
      moderationCaseStatus: null,
      businessPlan: 'Basic',
      flags: [],
      deletedOn: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'POST-7601',
      postId: 'POST-7601',
      title: 'Bathroom Waterproofing Specialists',
      businessName: 'Werribee Plumbing Pty Ltd',
      businessId: 'BUS-9934',
      category: 'Plumbing',
      subcategory: 'Bathroom Renovation',
      suburb: 'Werribee',
      postcode: '3030',
      serviceRadius: '15 km',
      appliedKeywords: ['Bathroom renovation', 'Waterproofing'],
      tier: 'Standard',
      postType: 'Post',
      status: 'Draft',
      visibility: 'Hidden',
      flagStatus: 'None',
      region: 'VIC',
      state: 'VIC',
      createdAt: new Date('2025-11-02T10:00:00.000Z').toISOString(),
      publishedOn: new Date('2025-11-02T10:20:00.000Z').toISOString(),
      lastUpdated: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      lastRefreshed: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      rankingState: 'Archived',
      views: 540,
      saves: 11,
      shares: 2,
      messages: 4,
      totalImages: 2,
      businessAccountStatus: 'Active',
      businessVerification: 'Verified',
      businessMemberSince: '2025-01-29',
      logo: 'https://i.pravatar.cc/100?img=12',
      thumbnail: 'https://picsum.photos/seed/post7601/64/64',
      publicLink: 'https://tradesposter.com/posts/POST-7601',
      description: 'Previously published bathroom waterproofing services. Returned to Draft after Confirmed Moderation.',
      gallery: [
        { url: 'https://picsum.photos/seed/post7601a/640/400', flagged: false },
        { url: 'https://picsum.photos/seed/post7601b/640/400', flagged: false },
      ],
      scenarioKey: null,
      moderationCaseId: null,
      moderationCaseStatus: null,
      businessPlan: 'Pro',
      flags: [],
    },
  ];
}

/** Posts list: scenario posts + retention samples (include flags for Post Detail → Flags tab) */
export function getPostsForScenarios() {
  const now = new Date(BASE);
  const scenarioPosts = scenarios.map((s, idx) => buildScenarioListPost(s, idx, now));
  return [...scenarioPosts, ...getRetentionSamplePosts(now)];
}

/** All scenarios (for Detail lookup, etc.) */
export { scenarios as postModerationScenarios };
